import { Badge } from "@/components/ui/badge";
import { Sparkle, WarningCircle, XCircle, Trash, ArrowLineUp, CopySimple } from "@phosphor-icons/react";

const TYPE_BADGE = {
  "Final Inspection": "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  Production: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]",
};

const Cell = ({ children, className = "" }) => (
  <td className={`px-2 py-1.5 align-middle ${className}`}>{children}</td>
);

const selectClass =
  "w-full h-8 rounded-md border border-[#D0D5DD] bg-white px-2 text-[12px] outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] transition-colors";

export const StepRow = ({ step, index, context, onChange, onRemove, onInsert, onDuplicate }) => (
  <tr className="border-b border-[#F2F4F7] hover:bg-[#FCFCFD] transition-colors" data-testid={`routing-step-row-${index}`}>
    <Cell className="text-[12px] text-[#667085] w-12">{step.step_no}</Cell>
    <Cell>
      <input
        value={step.description}
        onChange={(e) => onChange(index, { description: e.target.value })}
        data-testid={`routing-step-description-${index}`}
        className="w-full h-8 rounded-md border border-transparent hover:border-[#D0D5DD] focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] px-2 text-[13px] outline-none transition-colors bg-transparent"
      />
    </Cell>
    <Cell className="w-[170px]">
      <select
        value={step.step_type}
        onChange={(e) => onChange(index, { step_type: e.target.value, ai_classified: false })}
        data-testid={`routing-step-type-${index}`}
        className={selectClass}
      >
        {["Production", "Final Inspection"].map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
      {step.ai_classified && (
        <span
          className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-[#667085]"
          data-testid={`routing-step-ai-${index}`}
          title={`Classified automatically, confidence ${step.ai_confidence}`}
        >
          <Sparkle size={10} weight="fill" className="text-[#7F56D9]" />
          {Math.round((step.ai_confidence || 0) * 100)}% confident
        </span>
      )}
      {step.ai_failed && (
        <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-[#B54708]">
          <WarningCircle size={10} /> not classified
        </span>
      )}
    </Cell>
    <Cell className="w-[160px]">
      <select
        value={step.resource_id || ""}
        onChange={(e) => onChange(index, { resource_id: e.target.value })}
        data-testid={`routing-step-resource-${index}`}
        className={selectClass}
      >
        <option value="">Choose…</option>
        {(context?.resources || []).map((r) => (
          <option key={r.resource_id} value={r.resource_id}>{r.resource_id}</option>
        ))}
      </select>
      {step.logistics_area_id && (
        <span className="text-[10px] text-[#98A2B3]">Area {step.logistics_area_id}</span>
      )}
    </Cell>
    <Cell className="w-20">
      <input
        type="number"
        min="0.1"
        step="0.1"
        value={step.duration_minutes}
        onChange={(e) => onChange(index, { duration_minutes: e.target.value })}
        data-testid={`routing-step-duration-${index}`}
        className="w-full h-8 rounded-md border border-[#D0D5DD] px-2 text-[12px] outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] transition-colors"
      />
    </Cell>
    <Cell className="w-[220px]">
      <select
        value={step.component_label || ""}
        onChange={(e) => onChange(index, { component_label: e.target.value })}
        disabled={step.step_type === "Final Inspection"}
        data-testid={`routing-step-component-${index}`}
        className={`${selectClass} disabled:bg-[#F9FAFB] disabled:text-[#98A2B3]`}
      >
        <option value="">None</option>
        {(context?.item_groups || []).map((g) => (
          <option key={g.item_group_id} value={g.label}>{g.label}</option>
        ))}
      </select>
    </Cell>
    <Cell className="w-28 text-right whitespace-nowrap">
      <button
        type="button"
        onClick={() => onInsert(index)}
        data-testid={`routing-step-insert-${index}`}
        className="p-1.5 rounded-md text-[#98A2B3] hover:text-[#0E7C86] hover:bg-[#F0F9FF] transition-colors"
        title="Insert a step above this one"
      >
        <ArrowLineUp size={16} />
      </button>
      <button
        type="button"
        onClick={() => onDuplicate(index)}
        data-testid={`routing-step-duplicate-${index}`}
        className="p-1.5 rounded-md text-[#98A2B3] hover:text-[#0E7C86] hover:bg-[#F0F9FF] transition-colors"
        title="Duplicate this step"
      >
        <CopySimple size={16} />
      </button>
      <button
        type="button"
        onClick={() => onRemove(index)}
        data-testid={`routing-step-remove-${index}`}
        className="p-1.5 rounded-md text-[#98A2B3] hover:text-[#B42318] hover:bg-[#FEF3F2] transition-colors"
        title="Remove this step"
      >
        <Trash size={16} />
      </button>
    </Cell>
  </tr>
);

export const StepReviewTable = ({ steps, context, onChange, onRemove, onInsert, onDuplicate }) => (
  <div className="rounded-lg border border-[#D0D5DD] bg-white overflow-x-auto" data-testid="routing-step-table">
    <table className="w-full min-w-[760px] border-collapse">
      <thead>
        <tr className="bg-[#F9FAFB] border-b border-[#D0D5DD]">
          {["Step", "Description", "Step type", "Resource", "Min", "Backflush component", ""].map((h) => (
            <th key={h} className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-[#475467]">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {steps.map((step, index) => (
          <StepRow
            key={index}
            step={step}
            index={index}
            context={context}
            onChange={onChange}
            onRemove={onRemove}
            onInsert={onInsert}
            onDuplicate={onDuplicate}
          />
        ))}
      </tbody>
    </table>
  </div>
);

export const SapPlanPreview = ({ elements }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid="routing-sap-plan">
    {elements.map((element) => (
      <div key={element.element_id} className="flex items-center gap-1.5">
        <Badge
          variant="outline"
          className={`text-[10px] font-mono ${
            element.kind === "mark"
              ? "bg-[#F4F3FF] text-[#5925DC] border-[#D9D6FE]"
              : TYPE_BADGE[element.operation_type_label === "Check" ? "Final Inspection" : "Production"]
          }`}
          title={element.reason || `${element.operation_type_label} / ${element.activity_type_label}`}
        >
          {element.element_id}
          <span className="ml-1 opacity-70">
            {element.kind === "mark" ? "reporting point" : element.activity_type_label}
          </span>
        </Badge>
        <span className="text-[#D0D5DD] text-[10px]">→</span>
      </div>
    ))}
  </div>
);

export const IssueList = ({ errors = [], warnings = [] }) => {
  if (!errors.length && !warnings.length) return null;
  return (
    <div className="space-y-1.5" data-testid="routing-issues">
      {errors.map((text, i) => (
        <div
          key={`e${i}`}
          data-testid={`routing-error-${i}`}
          className="flex items-start gap-2 rounded-lg border border-[#FECDCA] bg-[#FEF3F2] px-3 py-2 text-[12px] text-[#B42318]"
        >
          <XCircle size={14} className="mt-0.5 shrink-0" />
          <span>{text}</span>
        </div>
      ))}
      {warnings.map((text, i) => (
        <div
          key={`w${i}`}
          data-testid={`routing-warning-${i}`}
          className="flex items-start gap-2 rounded-lg border border-[#FEDF89] bg-[#FFFAEB] px-3 py-2 text-[12px] text-[#B54708]"
        >
          <WarningCircle size={14} className="mt-0.5 shrink-0" />
          <span>{text}</span>
        </div>
      ))}
    </div>
  );
};
